import { useIntl } from 'react-intl';
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Button, H2 } from 'ech-components/src/primitives';
import { Grid, GridItemAnimated, Spacer } from 'ech-components/src/helpers';
import NavSection from '@components/NavSection';
import { createRoutingURI, getRootSlug } from '~/scripts/utils/domain';
import { publicationService } from '~/services';
import { rem } from 'polished';
import {
  Publication,
  TeaserContent,
  TeaserNewsCard,
  TeaserStorys,
  TeaserTools,
  Event,
} from 'ech-components';
import TeaserFunding from '../TeaserFunding/TeaserFunding';
import { mixins } from 'ech-components/src/core/styles/theme/default';
import { PublicationQueryProps } from '~/services/publicationService';

const RESULTS_PER_PAGE = 6;

const getTeaserByContentType = (contentType: string) => {
  switch (contentType) {
    case 'news':
      return TeaserNewsCard;
    case 'story':
      return TeaserStorys;
    case 'tool':
      return TeaserTools;
    case 'funding':
      return TeaserFunding;
    case 'publication':
      return Publication;
    case 'event':
      return Event;
    default:
      return TeaserContent;
  }
};

const getMetaByContentType = (contentType: string) => {
  switch (contentType) {
    case 'news':
      return 'search.sections.news.title';
    case 'story':
      return 'search.sections.stories.title';
    case 'tool':
      return 'search.sections.tools.title';
    case 'consultation':
      return 'search.sections.consultations.title';
    case 'funding':
      return 'search.sections.fundings.title';
    case 'publication':
      return 'search.sections.publications.title';
    case 'event':
      return 'search.sections.events.title';
    default:
      return 'search.sections.pages.title';
  }
};

const StyledGrid = styled(Grid)`
  ${mixins.responsiveStyles({
    'margin-bottom': {
      zero: `${rem(-16)}`,
      medium: `${rem(-24)}`,
      ultra: `${rem(-32)}`,
    },
  })}
`;

const StyledGridItemAnimated = styled(GridItemAnimated)`
  ${mixins.responsiveStyles({
    'margin-bottom': {
      zero: `${rem(16)}`,
      medium: `${rem(24)}`,
      ultra: `${rem(32)}`,
    },
  })}
`;

const StyledLoadMore = styled(Button)`
  display: block;
  padding: ${rem(10)} ${rem(26)};
  margin: 0 auto;
  height: auto;
  min-height: ${rem(56)};
  min-width: 0;
`;

const Hits = ({ hits }: { hits: object[] }) => {
  const [groupedResults, setGroupedResults] = useState<Array<any>>([]);
  const intl = useIntl();

  useEffect(() => {
    let tempGroupedResults: typeof groupedResults = [];

    hits.forEach((hit: { [key: string]: any }) => {
      const teaserData = { ...hit };
      const contentType = teaserData.contentType;

      let resultsGroup = tempGroupedResults.find(
        (resultsGroup: { [key: string]: any }) =>
          resultsGroup.contentType === contentType,
      );

      if (!resultsGroup) {
        resultsGroup = {
          id: contentType,
          contentType,
          teaser: getTeaserByContentType(contentType),
          visibleResults: RESULTS_PER_PAGE,
          items: [],
          meta: '',
        };

        tempGroupedResults.push(resultsGroup);
      }

      switch (contentType) {
        case 'story':
          teaserData.belongsToCampaign = true;

          if (teaserData.teaserImage) {
            teaserData.teaserImage.imageSizes = {
              0: [478, 450],
              480: [785, 450],
              768: [314, 450],
              1024: [396, 450],
              1280: [370, 450],
              1440: [426, 450],
            };
          }

          resultsGroup.items.push(teaserData);

          break;
        case 'news':
          teaserData.linkText = intl.formatMessage({
            id: 'hub.teaser.more.news',
          });

          resultsGroup.items.push(teaserData);

          break;
        case 'tool':
          teaserData.moreLinkText = intl.formatMessage({
            id: 'hub.teaser.more.tool',
          });

          resultsGroup.items.push(teaserData);

          break;
        case 'event':
          resultsGroup.items.push(teaserData);

          break;
        case 'publication': {
          const pubData = publicationService(
            teaserData as PublicationQueryProps,
          );

          if (pubData) {
            resultsGroup.items.push({ ...teaserData, ...pubData });
          }
          break;
        }
        case 'funding':
          teaserData.moreLinkText = intl.formatMessage({
            id: 'hub.teaser.more.fundings',
          });

          resultsGroup.items.push(teaserData);

          break;
        case 'consultation':
        default:
          teaserData.linkText = intl.formatMessage({
            id: 'hub.teaser.more.campaign',
          });

          if (teaserData.teaserImage) {
            teaserData.teaserImage.imageSizes = {
              0: [446, 174],
              480: [714, 286],
              768: [462, 278],
              1024: [376, 228],
              1280: [370, 185],
              1440: [426, 214],
            };
          }

          resultsGroup.items.push(teaserData);
      }
    });

    // update meta and items
    tempGroupedResults = tempGroupedResults.map((group) => ({
      ...group,
      items: group?.items?.map((item: { [key: string]: any }) => ({
        ...item,
        href: createRoutingURI(getRootSlug(item, intl.locale)),
      })),
      meta: intl.formatMessage(
        {
          id: getMetaByContentType(group.contentType),
        },
        {
          resultsCount: group?.items?.length,
        },
      ),
    }));

    setGroupedResults(tempGroupedResults);
  }, [hits, intl]);

  const loadMore = (contentType: string) => {
    const tempGroupedResults = [...groupedResults];
    const resultsGroup = tempGroupedResults.find(
      (resultsGroup: { contentType: string; visibleResults: number }) =>
        resultsGroup.contentType === contentType,
    );

    if (resultsGroup) {
      resultsGroup.visibleResults += RESULTS_PER_PAGE;
    }

    setGroupedResults(tempGroupedResults);
  };

  return (
    <>
      <Spacer space="giga" />
      <NavSection
        displayTitle={false}
        items={groupedResults}
        gridItemPush={0}
      />
      <Spacer space="kappa" />
      {groupedResults.map(
        (
          {
            contentType,
            items,
            teaser: Teaser,
            meta,
            visibleResults,
          }: {
            contentType: string;
            items: object[];
            teaser: React.ElementType;
            meta: string;
            visibleResults: number;
          },
          index,
        ) => {
          const gridItemSize =
            contentType === 'event'
              ? {
                  zero: 12,
                  ultra: 6,
                }
              : {
                  zero: 12,
                  medium: 6,
                  large: 4,
                };

          return (
            <section id={contentType} key={contentType}>
              <H2>{meta}</H2>
              <Spacer space="regular" />
              <StyledGrid columns={12}>
                {items
                  .slice(0, visibleResults)
                  .map((item: { [key: string]: any }) => (
                    <StyledGridItemAnimated
                      key={item.objectID}
                      size={gridItemSize}
                      push={0}
                      cascade
                      damping={2.5}
                      duration={700}
                      triggerOnce
                    >
                      <Teaser data={item} />
                    </StyledGridItemAnimated>
                  ))}
              </StyledGrid>
              {items.length > visibleResults ? (
                <>
                  <Spacer space="tera" />
                  <StyledLoadMore
                    outlined
                    onClick={() => loadMore(contentType)}
                    buttonText={intl.formatMessage({
                      id: 'search.loadMoreButton',
                    })}
                  />
                </>
              ) : null}
              {index < groupedResults.length - 1 && <Spacer space="kappa" />}
            </section>
          );
        },
      )}
    </>
  );
};

export default Hits;
